/**
 * 可拖动 + 可缩放的浮窗面板（P2-T1 优化，用户 2026-10-01：文本编辑框要跟随节点、
 * 细边框、能在页面里移动与自由缩放）。
 *
 * 通用件：标题栏按住可拖、右下角可拉伸尺寸，内容区自适应滚动。今后各类节点编辑面板
 * （图像/配音/视频）都复用它，风格一处统一。定位用 fixed 视口坐标，由调用方给初始 x/y。
 * 视觉走 token（细 border-surface-line 线条），不写死颜色。
 */
import { useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';

interface FloatingPanelProps {
  title: string;
  /** 初始视口坐标（左上角） */
  x: number;
  y: number;
  initialW?: number;
  initialH?: number;
  minWidth?: number;
  minHeight?: number;
  onClose: () => void;
  children: ReactNode;
}

export function FloatingPanel({
  title,
  x,
  y,
  initialW = 320,
  initialH = 300,
  minWidth = 240,
  minHeight = 160,
  onClose,
  children,
}: FloatingPanelProps) {
  const [pos, setPos] = useState({ x, y });
  const [size, setSize] = useState({ w: initialW, h: initialH });
  // 最大化编辑：展开到接近整屏便于大段文本编辑，再点还原到原尺寸位置（用户 2026-10-02）
  const [maximized, setMaximized] = useState(false);
  const restore = useRef<{ x: number; y: number; w: number; h: number } | null>(null);

  const toggleMax = () => {
    if (!maximized) {
      restore.current = { x: pos.x, y: pos.y, w: size.w, h: size.h };
      const w = Math.min(1200, window.innerWidth - 64);
      const h = window.innerHeight - 64;
      setPos({ x: (window.innerWidth - w) / 2, y: (window.innerHeight - h) / 2 });
      setSize({ w, h });
      setMaximized(true);
    } else {
      const r = restore.current;
      if (r !== null) {
        setPos({ x: r.x, y: r.y });
        setSize({ w: r.w, h: r.h });
      }
      setMaximized(false);
    }
  };

  const startDrag = (e: ReactPointerEvent) => {
    if (maximized) {
      return;
    }
    e.preventDefault();
    const sx = e.clientX;
    const sy = e.clientY;
    const ox = pos.x;
    const oy = pos.y;
    const move = (ev: PointerEvent) => setPos({ x: ox + ev.clientX - sx, y: oy + ev.clientY - sy });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const startResize = (e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const sx = e.clientX;
    const sy = e.clientY;
    const ow = size.w;
    const oh = size.h;
    const move = (ev: PointerEvent) =>
      setSize({
        w: Math.max(minWidth, ow + ev.clientX - sx),
        h: Math.max(minHeight, oh + ev.clientY - sy),
      });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div
      className={`floating-panel fixed z-50 flex flex-col overflow-hidden rounded-card border border-surface-line bg-surface-overlay shadow-[0_6px_18px_rgba(0,0,0,.35)] ${maximized ? 'is-max' : ''}`}
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
    >
      <div
        className={`flex touch-none select-none items-center justify-between gap-2 border-b border-surface-line bg-surface-raised px-3 py-1.5 ${maximized ? '' : 'cursor-move'}`}
        onPointerDown={startDrag}
      >
        <span className="truncate text-ui font-semibold">{title}</span>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label={maximized ? '还原' : '放大编辑'}
            title={maximized ? '还原' : '放大编辑'}
            className="text-uiSm text-ink-muted transition-colors hover:text-ink-primary"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={toggleMax}
          >
            {maximized ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
          <button
            type="button"
            aria-label="关闭"
            className="text-uiSm text-ink-muted transition-colors hover:text-ink-primary"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onClose}
          >
            ✕
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">{children}</div>
      {!maximized && (
        <div
          className="absolute bottom-0 right-0 flex h-4 w-4 cursor-nwse-resize touch-none items-end justify-end p-0.5"
          onPointerDown={startResize}
          title="拖动调整大小"
        >
          <span className="block h-2 w-2 rounded-br-sm border-b-2 border-r-2 border-surface-edge" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}
